import moment from 'moment'
import type {
  LabDataMock,
  LabResultNotificationRes,
  LatestResultNotification,
  PatientEmailStatus
} from '../types/lab.type'
import { INVALID_EMAIL_TEXT, isInvalidEmailError } from './patientEmail'

export const NOTIFICATION_POLL_MS = 5000
export const NOTIFICATION_POLL_MAX_MS = 2 * 60 * 1000

const REASON_TEXT: Record<string, string> = {
  email_not_allowed: 'Patient has opted out of email',
  feature_off: 'Emailing results is turned off',
  no_email: 'No email on file for this patient',
  no_results: 'Order has no results'
}

const ERROR_TEXT: Record<string, string> = {
  delivery_failed: 'the email could not be delivered',
  pdf_too_large: 'the PDF is larger than 15 MB',
  render_failed: 'the PDF could not be created'
}

/** Staff-facing copy for an ineligibility / skip reason code. */
export const reasonText = (reason?: string | null): string =>
  (reason && REASON_TEXT[reason]) || 'Patient is not eligible for email'

/** Staff-facing copy for a notification error code (unknown codes shown as-is). */
export const errorText = (error?: string | null): string => (error ? (ERROR_TEXT[error] ?? error) : 'unknown error')

export const isNotificationInFlight = (n?: LatestResultNotification | null): boolean =>
  n?.status === 'queued' || n?.status === 'sending'

export type ResultEmailTone = 'success' | 'pending' | 'warning' | 'error'

export function notificationStatusLine(
  n?: LatestResultNotification | null
): { text: string; tone: ResultEmailTone } | null {
  if (!n) return null
  const corrected = n.kind === 'correction'
  switch (n.status) {
    case 'sent': {
      const when = n.sent_at ? ` · ${moment(n.sent_at).format('MMM D, YYYY h:mm A')}` : ''
      const what = corrected ? 'Corrected results' : 'Results'
      return { text: `${what} emailed to ${n.recipient_email ?? 'patient'}${when}`, tone: 'success' }
    }
    case 'queued':
    case 'sending':
      return { text: corrected ? 'Sending corrected results…' : 'Sending results…', tone: 'pending' }
    case 'skipped':
      return { text: `Not sent: ${reasonText(n.skip_reason)}`, tone: 'warning' }
    case 'failed':
      return { text: `Sending failed: ${errorText(n.error)}`, tone: 'error' }
    default:
      return null
  }
}

/**
 * Status line for the order page: the latest notification's line; or, for a completed order that was
 * never emailed because the patient is ineligible, "Not sent: {reason}". null = nothing to report.
 */
export function resultEmailStatusLine({
  emailStatus,
  notification,
  orderCompleted
}: {
  emailStatus?: PatientEmailStatus
  notification?: LatestResultNotification | null
  orderCompleted: boolean
}): { text: string; tone: ResultEmailTone } | null {
  if (notification) return notificationStatusLine(notification)
  if (orderCompleted && emailStatus?.enabled && !emailStatus.eligible) {
    return { text: `Not sent: ${reasonText(emailStatus.reason)}`, tone: 'warning' }
  }
  return null
}

/** Email attributes from a lab order row; legacy `order` rows never have them. */
export function emailInfoFromRow(row?: LabDataMock): {
  emailStatus: PatientEmailStatus | undefined
  notification: LatestResultNotification | null
} {
  if (!row || row.type !== 'lab_order') return { emailStatus: undefined, notification: null }
  return {
    emailStatus: row.attributes.patient_email_status,
    notification: row.attributes.latest_result_notification ?? null
  }
}

/**
 * Value to send as `send_results_to_patient`.
 * undefined = omit the param (feature off / backend not deployed); otherwise eligible && checked.
 */
export function sendResultsFlag(status: PatientEmailStatus | undefined, checked: boolean): boolean | undefined {
  if (!status?.enabled) return undefined
  return status.eligible && checked
}

export function canSendResults({
  emailStatus,
  notification,
  orderCompleted
}: {
  emailStatus?: PatientEmailStatus
  notification?: LatestResultNotification | null
  orderCompleted: boolean
}): boolean {
  return orderCompleted && !!emailStatus?.enabled && emailStatus.eligible && !isNotificationInFlight(notification)
}

/** Adds `data.attributes.send_results_to_patient` to a JSON:API body (no-op when flag is undefined). */
export function withSendResultsFlag<T extends { data: { attributes: object } }>(
  payload: T,
  flag: boolean | undefined
): T {
  if (flag === undefined) return payload
  return {
    ...payload,
    data: { ...payload.data, attributes: { ...payload.data.attributes, send_results_to_patient: flag } }
  }
}

export type NotificationPollState = { id: string | null; startedAt: number | null }

/** refetchInterval for the single lab order: every 5 s while queued/sending, for at most ~2 min per notification. */
export function nextNotificationPoll(
  n: LatestResultNotification | null | undefined,
  prev: NotificationPollState,
  now: number
): { interval: number | false; state: NotificationPollState } {
  if (!n || !isNotificationInFlight(n)) return { interval: false, state: { id: null, startedAt: null } }
  const id = String(n.id)
  const startedAt = prev.id === id && prev.startedAt != null ? prev.startedAt : now
  return {
    interval: now - startedAt < NOTIFICATION_POLL_MAX_MS ? NOTIFICATION_POLL_MS : false,
    state: { id, startedAt }
  }
}

export function sendResultsToast(res: LabResultNotificationRes | undefined): string {
  const attrs = res?.data?.attributes
  if (attrs?.status === 'skipped') return `Not sent: ${reasonText(attrs.skip_reason)}`
  return 'Sending results to patient'
}

export function sendResultsErrorMessage(error: unknown): string {
  const err = error as { response?: { data?: { errors?: { code?: string; detail?: string }[] } } }
  const first = err?.response?.data?.errors?.[0]
  if (first?.code === 'order_not_completed') return 'Complete the order before sending results'
  return first?.detail || 'Failed to send results'
}

/** "Add email" applies: feature on, not eligible only because there is no email. */
export const canAddEmail = (status?: PatientEmailStatus): boolean =>
  !!status?.enabled && !status.eligible && status.reason === 'no_email'

export const addEmailErrorMessage = (error: unknown): string =>
  isInvalidEmailError(error) ? INVALID_EMAIL_TEXT : 'Could not save the email'
